<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import FollowUpCompleteButton from 'dashboard/components-next/FollowUp/FollowUpCompleteButton.vue';
import {
  useFollowUp,
  getFollowUpBadge,
  formatFollowUpDate,
  formatFollowUpTime,
} from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';
import { useMeetingScheduler } from 'dashboard/components-next/FollowUp/composables/useMeetingScheduler.js';
import {
  FOLLOW_UP_TYPES,
  FOLLOW_UP_TYPE_LIST,
  MEETING_SLOTS,
  DEFAULT_BUSINESS_TIMEZONE,
  isMeetingType,
  isValidMeetingSlot,
  resolveFollowUpType,
  zonedDateTimeToIso,
  isoToZonedParts,
} from 'dashboard/components-next/FollowUp/helpers/meetingSlots.js';

const props = defineProps({
  mode: {
    type: String,
    default: 'compact',
    validator: value => ['compact', 'full', 'summary'].includes(value),
  },
  contactId: { type: Number, required: true },
  // Whether this contact has at least one conversation — the only signal
  // needed to know if agent reassignment is possible; the backend resolves
  // the actual conversation itself, so no id is ever passed through here.
  hasConversation: { type: Boolean, default: false },
  // Only needed to gate the "Schedule Meeting" action — a scheduling
  // provider needs an attendee email. Never sent anywhere from here; the
  // backend reads the contact's own email when it actually books.
  email: { type: String, default: '' },
  nextFollowUpAt: { type: String, default: '' },
  followUpNote: { type: String, default: '' },
  meetingRequired: { type: Boolean, default: false },
  meetingProvider: { type: String, default: '' },
  meetingUrl: { type: String, default: '' },
  meetingReference: { type: String, default: '' },
  // Follow-up type fields (see Nexceo::FollowUps::MeetingSlots). Rows saved before types existed
  // have none of these; the type is then derived from meetingRequired.
  followUpType: { type: String, default: '' },
  meetingLocation: { type: String, default: '' },
  followUpTeamId: { type: [Number, String], default: '' },
  assignee: { type: Object, default: null },
  ccEmails: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();
const { saveFollowUp, cancelFollowUp, isSaving } = useFollowUp();
const {
  isScheduling,
  schedulingProviders,
  isProviderConnected,
  providerAction,
  scheduleMeeting,
  rescheduleMeeting,
  cancelMeeting,
} = useMeetingScheduler();

const badge = computed(() => getFollowUpBadge(props.nextFollowUpAt));
const formattedDate = computed(() => formatFollowUpDate(props.nextFollowUpAt));
const formattedTime = computed(() => formatFollowUpTime(props.nextFollowUpAt));

const MEETING_PROVIDERS = [
  'Zoom',
  'Google Meet',
  'Microsoft Teams',
  'Manual / Other',
];
// The manual (free-text) providers plus whatever the provider layer reports
// as capable of real scheduling — this component has no idea what any of
// the latter *are*, only that they came back from useMeetingScheduler.
const meetingProviderOptions = computed(() => [
  ...MEETING_PROVIDERS.map(label => ({ label, value: label })),
  ...schedulingProviders.value.map(provider => ({
    label: provider.label,
    value: provider.id,
  })),
]);

const providerLabel = id =>
  schedulingProviders.value.find(provider => provider.id === id)?.label || id;

const pad = num => String(num).padStart(2, '0');
const toDateInputValue = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const toTimeInputValue = date =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const combineDateAndTime = (dateStr, timeStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = (timeStr || '00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes).toISOString();
};

// Matches Contact::CC_EMAILS_MAX_COUNT server-side — kept in sync manually
// since the two layers don't share constants across the JS/Ruby boundary.
const CC_EMAILS_MAX_COUNT = 10;

const draftType = ref(FOLLOW_UP_TYPES.OPEN_FOLLOW_UP);
const draftDate = ref('');
const draftTime = ref('');
const draftNote = ref('');
const draftAgentId = ref('');
const draftTeamId = ref('');
const draftLocation = ref('');
const draftMeetingProvider = ref('');
const draftMeetingUrl = ref('');
const draftMeetingReference = ref('');
const draftCcEmails = ref([]);
// What was loaded, so a legacy off-grid meeting time is only rejected once it is actually edited.
const initialType = ref(FOLLOW_UP_TYPES.OPEN_FOLLOW_UP);
const initialTime = ref('');

// Meeting slots are business-time slots in the account's zone (the same zone the backend validates
// in) - never the browser's. Open follow-ups keep the browser-local date/time they always had.
const businessTimezone = computed(
  () => currentAccount.value?.business_timezone || DEFAULT_BUSINESS_TIMEZONE
);
const isMeeting = computed(() => isMeetingType(draftType.value));
const isOnline = computed(
  () => draftType.value === FOLLOW_UP_TYPES.ONLINE_MEETING
);
const isPhysical = computed(
  () => draftType.value === FOLLOW_UP_TYPES.PHYSICAL_MEETING
);

const partsFromIso = (iso, type) => {
  if (!iso) return { date: '', time: '' };
  if (isMeetingType(type)) return isoToZonedParts(iso, businessTimezone.value);
  const local = new Date(iso);
  return { date: toDateInputValue(local), time: toTimeInputValue(local) };
};

const resetDraft = () => {
  const type = resolveFollowUpType({
    followUpType: props.followUpType,
    meetingRequired: props.meetingRequired,
  });
  const parts = partsFromIso(props.nextFollowUpAt, type);
  draftType.value = type;
  initialType.value = type;
  draftDate.value = parts.date;
  draftTime.value = parts.time;
  initialTime.value = parts.time;
  draftNote.value = props.followUpNote;
  draftAgentId.value = props.assignee ? String(props.assignee.id) : '';
  draftTeamId.value = props.followUpTeamId ? String(props.followUpTeamId) : '';
  draftLocation.value = props.meetingLocation;
  draftMeetingProvider.value = props.meetingProvider;
  draftMeetingUrl.value = props.meetingUrl;
  draftMeetingReference.value = props.meetingReference;
  draftCcEmails.value = [...(props.ccEmails || [])];
};

// TagInput's own dedup (`!tags.includes(item.label)`) is case-sensitive, so
// "Manager@x.com" and "manager@x.com" would otherwise both land in the
// list. Normalize + dedupe here rather than in the shared component, since
// only this consumer needs case-insensitivity, and enforce the same
// recipient cap the server validates so the failure never has to make a
// round trip.
const handleCcUpdate = value => {
  const seen = new Set();
  const deduped = [];
  value.forEach(raw => {
    const trimmed = raw.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    deduped.push(trimmed);
  });
  draftCcEmails.value = deduped.slice(0, CC_EMAILS_MAX_COUNT);
};

// Compare assignee by id, not object reference — Vuex getters that
// camelCase contact data (e.g. contacts/getContactById) return a new
// object on every computation, so watching the object itself would refire
// on unrelated store changes and silently wipe in-progress edits.
watch(
  () => [
    props.nextFollowUpAt,
    props.followUpNote,
    props.assignee?.id,
    props.meetingRequired,
    props.meetingProvider,
    props.meetingUrl,
    props.meetingReference,
    props.followUpType,
    props.meetingLocation,
    props.followUpTeamId,
    props.ccEmails,
  ],
  resetDraft,
  { immediate: true }
);

const agents = useMapGetter('agents/getAgents');
const agentOptions = computed(() =>
  agents.value.map(agent => ({ label: agent.name, value: String(agent.id) }))
);

const teams = useMapGetter('teams/getTeams');
const teamOptions = computed(() =>
  (teams.value || []).map(team => ({
    label: team.name,
    value: String(team.id),
  }))
);
if (!(teams.value || []).length) store.dispatch('teams/get');

// True once the agent has picked a provider the provider layer reports as
// schedulable — never a check against a specific provider's name.
const isSchedulableProvider = computed(() =>
  schedulingProviders.value.some(
    provider => provider.id === draftMeetingProvider.value
  )
);
const selectedProviderConnected = computed(() =>
  isProviderConnected(draftMeetingProvider.value)
);
// A provider-backed meeting already exists — the time can only change via
// "Reschedule" so the provider's own calendar never silently drifts from
// what's shown here.
const isMeetingLocked = computed(
  () =>
    isOnline.value &&
    isSchedulableProvider.value &&
    selectedProviderConnected.value &&
    !!draftMeetingReference.value
);
// Meetings: date + slot read as business-time; open follow-ups: browser-local, as before.
const startAtIso = () =>
  isMeeting.value
    ? zonedDateTimeToIso(
        draftDate.value,
        draftTime.value,
        businessTimezone.value
      )
    : combineDateAndTime(draftDate.value, draftTime.value);

// Mirrors the backend rule (Nexceo::FollowUps::AttributeValidator) so the UI never submits a time
// the server would reject: a meeting with a date needs a slot from the grid - unless it is the
// untouched legacy time of an existing meeting - and a physical meeting needs a location.
const slotError = computed(() => {
  if (!isMeeting.value || !draftDate.value) return '';
  if (!draftTime.value) return t('FOLLOW_UP.MEETING.SELECT_TIME_HINT');
  const untouchedLegacy =
    draftTime.value === initialTime.value &&
    draftType.value === initialType.value;
  return isValidMeetingSlot(draftTime.value) || untouchedLegacy
    ? ''
    : t('FOLLOW_UP.MEETING.INVALID_TIME');
});
const locationMissing = computed(
  () => isPhysical.value && !!draftDate.value && !draftLocation.value.trim()
);
const canSave = computed(() => !slotError.value && !locationMissing.value);

const canScheduleMeeting = computed(
  () =>
    !!draftDate.value && !!draftTime.value && !slotError.value && !!props.email
);

const handleTypeChange = tab => {
  draftType.value = tab.value;
  // Cal.com is the default provider for online meetings - never a blank choice when it exists.
  if (isOnline.value && !draftMeetingProvider.value) {
    const [defaultProvider] = schedulingProviders.value;
    if (defaultProvider) draftMeetingProvider.value = defaultProvider.id;
  }
  // Fields that belong to another type are dropped rather than silently saved.
  if (!isOnline.value) {
    draftMeetingProvider.value = '';
    draftMeetingUrl.value = '';
    draftMeetingReference.value = '';
    draftCcEmails.value = [];
  }
  if (!isPhysical.value) draftLocation.value = '';
  if (isMeeting.value && !isValidMeetingSlot(draftTime.value)) {
    draftTime.value = '';
  }
};

const handleSave = async () => {
  if (!canSave.value) return;
  const saved = await saveFollowUp({
    contactId: props.contactId,
    assignedAgentId: draftAgentId.value || null,
    nextFollowUpAt: startAtIso(),
    followUpNote: draftNote.value,
    meetingRequired: isMeeting.value,
    meetingProvider: isOnline.value ? draftMeetingProvider.value : '',
    meetingUrl: isOnline.value ? draftMeetingUrl.value : '',
    meetingReference: isOnline.value ? draftMeetingReference.value : '',
    ccEmails: isOnline.value ? draftCcEmails.value : [],
    followUpType: draftType.value,
    followUpStatus: 'scheduled',
    meetingLocation: isPhysical.value ? draftLocation.value.trim() : '',
    followUpTeamId: draftTeamId.value || null,
  });
  if (saved) emit('saved');
};

const handleScheduleMeeting = async () => {
  if (!canSave.value) return;
  const scheduled = await scheduleMeeting(props.contactId, {
    provider: draftMeetingProvider.value,
    startAt: startAtIso(),
    assignedAgentId: draftAgentId.value || null,
  });
  if (scheduled) emit('saved');
};

const handleRescheduleMeeting = async () => {
  if (!canSave.value) return;
  const rescheduled = await rescheduleMeeting(props.contactId, {
    startAt: startAtIso(),
  });
  if (rescheduled) emit('saved');
};

// A saved (not provider-booked) meeting that will not happen: the explicit `cancelled` status is
// what notifies the customer and staff - "Complete" never does.
const hasSavedManualMeeting = computed(
  () =>
    !!props.nextFollowUpAt &&
    isMeetingType(initialType.value) &&
    !props.meetingReference
);
const handleCancelManualMeeting = async () => {
  const cancelled = await cancelFollowUp(props.contactId);
  if (cancelled) emit('saved');
};

const handleCancelMeeting = async () => {
  const cancelled = await cancelMeeting(props.contactId);
  if (cancelled) emit('saved');
};

// Static keys on purpose (no `t(`...${type}`)`): keeps the labels greppable and lint-clean.
const typeLabel = type =>
  ({
    [FOLLOW_UP_TYPES.ONLINE_MEETING]: t('FOLLOW_UP.TYPES.ONLINE_MEETING'),
    [FOLLOW_UP_TYPES.OPEN_FOLLOW_UP]: t('FOLLOW_UP.TYPES.OPEN_FOLLOW_UP'),
    [FOLLOW_UP_TYPES.PHYSICAL_MEETING]: t('FOLLOW_UP.TYPES.PHYSICAL_MEETING'),
  })[type];

const typeTabs = computed(() =>
  FOLLOW_UP_TYPE_LIST.map(type => ({ label: typeLabel(type), value: type }))
);
const activeTypeIndex = computed(() =>
  FOLLOW_UP_TYPE_LIST.indexOf(draftType.value)
);

// Exactly the 25 slots. The one exception: the untouched legacy time of an existing meeting is
// shown (flagged) instead of silently vanishing from the dropdown.
const slotOptions = computed(() => {
  const options = MEETING_SLOTS.map(slot => ({ label: slot, value: slot }));
  if (draftTime.value && !isValidMeetingSlot(draftTime.value)) {
    options.unshift({
      label: t('FOLLOW_UP.MEETING.LEGACY_TIME', { time: draftTime.value }),
      value: draftTime.value,
    });
  }
  return options;
});

const summaryTypeLabel = computed(() =>
  typeLabel(
    resolveFollowUpType({
      followUpType: props.followUpType,
      meetingRequired: props.meetingRequired,
    })
  )
);
const summaryIsPhysical = computed(
  () => props.followUpType === FOLLOW_UP_TYPES.PHYSICAL_MEETING
);

const handleCompleted = () => emit('saved');

// Summary mode owns its own edit affordance (pencil, complete/schedule
// button, and this dialog) so every surface that renders `mode="summary"`
// (Contact page, Conversation sidebar) gets identical actions for free
// instead of each parent wiring its own copy.
const editDialogRef = ref(null);
const openEditDialog = () => editDialogRef.value?.open();
const handleDialogSaved = () => {
  editDialogRef.value?.close();
  emit('saved');
};

const handleCopyLink = async () => {
  await copyTextToClipboard(props.meetingUrl);
  useAlert(t('FOLLOW_UP.MEETING.LINK_COPIED'));
};
const handleOpenMeeting = () => {
  window.open(props.meetingUrl, '_blank', 'noopener');
};
</script>

<template>
  <div
    v-if="mode === 'compact'"
    class="flex items-center flex-wrap gap-x-2.5 gap-y-1 text-xs text-n-slate-11"
  >
    <template v-if="formattedDate">
      <span class="inline-flex items-center gap-1">
        <Icon icon="i-lucide-calendar" class="size-3 text-n-slate-9" />
        {{ formattedDate }}
      </span>
      <span class="inline-flex items-center gap-1">
        <Icon icon="i-lucide-clock" class="size-3 text-n-slate-9" />
        {{ formattedTime }}
      </span>
      <Label
        v-if="badge"
        :label="t(badge.labelKey)"
        :color="badge.color"
        compact
        class="ml-auto"
      />
    </template>
  </div>

  <div v-else-if="mode === 'summary'" class="flex flex-col gap-1.5 text-sm">
    <div v-if="formattedDate" class="flex items-center gap-2 text-n-slate-12">
      <Icon icon="i-lucide-calendar-clock" class="size-3.5 text-n-slate-9" />
      <span>{{ formattedDate }} · {{ formattedTime }}</span>
      <Label
        v-if="badge"
        :label="t(badge.labelKey)"
        :color="badge.color"
        compact
        class="ml-auto"
      />
    </div>
    <div v-else class="text-n-slate-10">
      {{ t('FOLLOW_UP.NONE_SCHEDULED') }}
    </div>
    <div v-if="assignee" class="flex items-center gap-2 text-n-slate-11">
      <Avatar :name="assignee.name" :src="assignee.thumbnail" :size="16" />
      <span>{{ t('FOLLOW_UP.LABELS.ASSIGNED_TO') }} {{ assignee.name }}</span>
    </div>
    <div
      v-if="nextFollowUpAt"
      class="flex items-center gap-2 text-n-slate-11"
      data-testid="follow-up-type-label"
    >
      <Icon
        :icon="summaryIsPhysical ? 'i-lucide-map-pin' : 'i-lucide-list-checks'"
        class="flex-shrink-0 size-3.5 text-n-slate-9"
      />
      <span class="truncate">{{ summaryTypeLabel }}</span>
    </div>
    <div
      v-if="summaryIsPhysical && meetingLocation"
      class="flex items-center gap-2 text-n-slate-11"
    >
      <Icon
        icon="i-lucide-map-pin"
        class="flex-shrink-0 size-3.5 text-n-slate-9"
      />
      <span class="truncate">{{ meetingLocation }}</span>
    </div>
    <div
      v-if="meetingRequired && !summaryIsPhysical"
      class="flex items-center gap-2 text-n-slate-11"
    >
      <Icon
        icon="i-lucide-video"
        class="flex-shrink-0 size-3.5 text-n-slate-9"
      />
      <span class="truncate">
        {{
          [providerLabel(meetingProvider), meetingUrl]
            .filter(Boolean)
            .join(' · ') || t('FOLLOW_UP.LABELS.MEETING_REQUIRED')
        }}
      </span>
      <Label
        v-if="meetingUrl"
        :label="t('FOLLOW_UP.BADGE.MEETING_SCHEDULED')"
        color="iris"
        compact
      />
      <div
        v-if="meetingUrl"
        class="flex items-center flex-shrink-0 gap-1 ml-auto"
      >
        <Button
          v-tooltip="t('FOLLOW_UP.MEETING.COPY_LINK')"
          icon="i-lucide-copy"
          variant="ghost"
          color="slate"
          size="xs"
          @click="handleCopyLink"
        />
        <Button
          v-tooltip="t('FOLLOW_UP.MEETING.OPEN_MEETING')"
          icon="i-lucide-external-link"
          variant="ghost"
          color="slate"
          size="xs"
          @click="handleOpenMeeting"
        />
      </div>
    </div>
    <div v-if="followUpNote" class="flex items-start gap-2 text-n-slate-11">
      <Icon
        icon="i-lucide-sticky-note"
        class="flex-shrink-0 mt-0.5 size-3.5 text-n-slate-9"
      />
      <span class="italic line-clamp-2">{{ followUpNote }}</span>
    </div>

    <div class="flex items-center justify-between pt-1">
      <FollowUpCompleteButton
        v-if="nextFollowUpAt"
        :contact-id="contactId"
        size="xs"
        @completed="handleCompleted"
        @reschedule="openEditDialog"
      />
      <Button
        v-else
        :label="t('FOLLOW_UP.ACTIONS.SCHEDULE')"
        size="xs"
        variant="faded"
        color="slate"
        @click="openEditDialog"
      />
      <Button
        icon="i-lucide-pencil"
        variant="ghost"
        color="slate"
        size="xs"
        @click="openEditDialog"
      />
    </div>

    <Dialog
      ref="editDialogRef"
      type="edit"
      :title="t('FOLLOW_UP.TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="t('FOLLOW_UP.ACTIONS.CLOSE')"
    >
      <FollowUp
        mode="full"
        :contact-id="contactId"
        :has-conversation="hasConversation"
        :email="email"
        :next-follow-up-at="nextFollowUpAt"
        :follow-up-note="followUpNote"
        :meeting-required="meetingRequired"
        :meeting-provider="meetingProvider"
        :meeting-url="meetingUrl"
        :meeting-reference="meetingReference"
        :follow-up-type="followUpType"
        :meeting-location="meetingLocation"
        :follow-up-team-id="followUpTeamId"
        :assignee="assignee"
        :cc-emails="ccEmails"
        @saved="handleDialogSaved"
      />
    </Dialog>
  </div>

  <div v-else class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('FOLLOW_UP.LABELS.TYPE') }}
      </label>
      <TabBar
        :tabs="typeTabs"
        :initial-active-tab="activeTypeIndex"
        data-testid="follow-up-type-tabs"
        @tab-changed="handleTypeChange"
      />
    </div>

    <div
      v-if="isOnline"
      class="flex flex-col gap-1"
      data-testid="meeting-provider-field"
    >
      <label class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('FOLLOW_UP.LABELS.MEETING_PROVIDER') }}
      </label>
      <ComboBox
        v-model="draftMeetingProvider"
        :options="meetingProviderOptions"
        :placeholder="t('FOLLOW_UP.LABELS.MEETING_PROVIDER')"
      />
    </div>

    <div class="grid grid-cols-2 gap-3">
      <Input
        v-model="draftDate"
        type="date"
        :label="t('FOLLOW_UP.LABELS.DATE')"
        :disabled="isMeetingLocked"
      />
      <div
        v-if="isMeeting"
        class="flex flex-col gap-1"
        data-testid="meeting-time-field"
      >
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('FOLLOW_UP.LABELS.TIME') }}
        </label>
        <Select
          v-model="draftTime"
          :options="slotOptions"
          :placeholder="t('FOLLOW_UP.MEETING.SELECT_TIME')"
          :disabled="isMeetingLocked"
          :error="slotError"
        />
      </div>
      <Input
        v-else
        v-model="draftTime"
        type="time"
        :label="t('FOLLOW_UP.LABELS.TIME')"
      />
    </div>
    <p v-if="isMeeting" class="-mt-3 text-xs text-n-slate-10">
      {{ t('FOLLOW_UP.MEETING.SLOT_HINT', { timezone: businessTimezone }) }}
    </p>
    <p v-if="slotError" class="-mt-3 text-xs text-n-ruby-11">
      {{ slotError }}
    </p>
    <p v-if="isMeetingLocked" class="-mt-3 text-xs text-n-slate-10">
      {{ t('FOLLOW_UP.MEETING.LOCKED_HINT') }}
    </p>

    <div
      v-if="isOnline && isSchedulableProvider"
      class="flex flex-col gap-3"
      data-testid="meeting-booking-block"
    >
      <p v-if="!selectedProviderConnected" class="text-xs text-n-slate-10">
        {{ t('FOLLOW_UP.MEETING.NOT_CONNECTED') }}
        <a
          :href="providerAction(draftMeetingProvider)"
          class="text-n-brand hover:underline"
        >
          {{ t('FOLLOW_UP.MEETING.CONNECT_CALENDAR') }}
        </a>
      </p>

      <template v-else-if="draftMeetingReference">
        <a
          :href="draftMeetingUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm truncate text-n-brand hover:underline"
        >
          {{ draftMeetingUrl }}
        </a>
        <div class="flex items-center gap-2">
          <Button
            :label="t('FOLLOW_UP.MEETING.RESCHEDULE')"
            size="sm"
            variant="faded"
            color="slate"
            :is-loading="isScheduling"
            :disabled="!canScheduleMeeting"
            @click="handleRescheduleMeeting"
          />
          <Button
            :label="t('FOLLOW_UP.MEETING.CANCEL')"
            size="sm"
            variant="faded"
            color="ruby"
            :is-loading="isScheduling"
            @click="handleCancelMeeting"
          />
        </div>
      </template>

      <template v-else>
        <Button
          :label="t('FOLLOW_UP.MEETING.SCHEDULE')"
          size="sm"
          :is-loading="isScheduling"
          :disabled="!canScheduleMeeting"
          @click="handleScheduleMeeting"
        />
        <p v-if="!email" class="text-xs text-n-slate-10">
          {{ t('FOLLOW_UP.MEETING.EMAIL_REQUIRED_HINT') }}
        </p>
      </template>
    </div>

    <div
      v-else-if="isOnline"
      class="flex flex-col gap-3"
      data-testid="meeting-manual-link-block"
    >
      <Input
        v-model="draftMeetingUrl"
        :label="t('FOLLOW_UP.LABELS.MEETING_URL')"
        placeholder="https://..."
      />
      <Input
        v-model="draftMeetingReference"
        :label="t('FOLLOW_UP.LABELS.MEETING_REFERENCE')"
      />
    </div>

    <div
      v-if="isPhysical"
      class="flex flex-col gap-1"
      data-testid="meeting-location-field"
    >
      <Input
        v-model="draftLocation"
        :label="t('FOLLOW_UP.LABELS.LOCATION')"
        :placeholder="t('FOLLOW_UP.LABELS.LOCATION_PLACEHOLDER')"
      />
      <p v-if="locationMissing" class="text-xs text-n-ruby-11">
        {{ t('FOLLOW_UP.MEETING.LOCATION_REQUIRED') }}
      </p>
    </div>

    <div class="flex flex-col gap-1" data-testid="follow-up-agent-field">
      <label class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('FOLLOW_UP.LABELS.ASSIGNED_AGENT') }}
      </label>
      <ComboBox
        v-model="draftAgentId"
        :options="agentOptions"
        :placeholder="t('FOLLOW_UP.LABELS.ASSIGNED_AGENT')"
        :disabled="!hasConversation"
      />
      <p v-if="!hasConversation" class="mt-1 mb-0 text-xs text-n-slate-10">
        {{ t('FOLLOW_UP.NO_CONVERSATION_HINT') }}
      </p>
    </div>

    <div class="flex flex-col gap-1" data-testid="follow-up-team-field">
      <label class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('FOLLOW_UP.LABELS.TEAM') }}
      </label>
      <ComboBox
        v-model="draftTeamId"
        :options="teamOptions"
        :placeholder="t('FOLLOW_UP.LABELS.TEAM')"
      />
    </div>

    <div
      v-if="isOnline"
      class="flex flex-col gap-1"
      data-testid="follow-up-cc-field"
    >
      <label class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('FOLLOW_UP.LABELS.CC_RECIPIENTS') }}
      </label>
      <TagInput
        :model-value="draftCcEmails"
        type="email"
        mode="multiple"
        allow-create
        :placeholder="t('FOLLOW_UP.LABELS.CC_RECIPIENTS_PLACEHOLDER')"
        @update:model-value="handleCcUpdate"
      />
      <p
        v-if="draftCcEmails.length >= CC_EMAILS_MAX_COUNT"
        class="mt-1 mb-0 text-xs text-n-slate-10"
      >
        {{
          t('FOLLOW_UP.LABELS.CC_RECIPIENTS_MAX_HINT', {
            max: CC_EMAILS_MAX_COUNT,
          })
        }}
      </p>
    </div>

    <TextArea
      v-model="draftNote"
      :label="
        isMeeting ? t('FOLLOW_UP.LABELS.NOTES') : t('FOLLOW_UP.LABELS.NOTE')
      "
      :placeholder="t('FOLLOW_UP.LABELS.NOTE_PLACEHOLDER')"
      rows="3"
    />

    <div v-if="hasSavedManualMeeting" class="flex items-center gap-2">
      <Button
        :label="t('FOLLOW_UP.MEETING.CANCEL')"
        size="sm"
        variant="faded"
        color="ruby"
        :is-loading="isSaving"
        data-testid="cancel-manual-meeting"
        @click="handleCancelManualMeeting"
      />
    </div>

    <div class="flex items-center justify-between pt-2">
      <FollowUpCompleteButton
        v-if="nextFollowUpAt"
        :contact-id="contactId"
        size="sm"
        @completed="handleCompleted"
      />
      <span v-else />
      <Button
        :label="t('FOLLOW_UP.ACTIONS.SAVE')"
        size="sm"
        :is-loading="isSaving"
        :disabled="!canSave"
        @click="handleSave"
      />
    </div>
  </div>
</template>
